/**
 * Fait : l'écran « mot de passe oublié » — on donne son adresse, un courriel
 *   part. La réponse affichée est la MÊME que l'adresse ait un compte ou non,
 *   parce que c'est la même que le serveur donne : l'écran ne rétablit pas
 *   l'aveu que la route a pris soin d'effacer.
 *
 * Ne fait pas : il ne dit jamais « adresse inconnue », ne montre aucun jeton,
 *   et ne choisit pas le nouveau mot de passe — cela se passe sur l'écran
 *   d'après, celui du lien reçu par courriel.
 */

import { useState } from 'react';

import { DUREE_DU_LIEN_DE_RECUPERATION_HEURES } from '@aymoai/shared';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import { demandeUnLienDOubli } from './api.ts';

/** L'aide de l'écran, sortie du JSX pour tenir dans la largeur du code. */
const AIDE =
  'Donnez votre adresse électronique : vous recevrez un courriel avec un lien ' +
  'pour choisir un nouveau mot de passe.';

export function PageOubli() {
  const [adresse, setAdresse] = useState('');
  const [demande, setDemande] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function demandeLeLien(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setEnCours(true);
    setEchec(null);

    const issue = await demandeUnLienDOubli(adresse.trim());
    setEnCours(false);

    if (issue.bon) {
      setDemande(true);
      return;
    }

    setEchec(issue.erreur.message);
  }

  return (
    <div className="empilement" data-etat={demande ? 'nominal' : 'vide'} data-ecran="oubli">
      <Partie
        etiquette="Retrouver son accès"
        nature="gens"
        titre="Mot de passe oublié"
        niveauDeTitre={2}
        aide={AIDE}
        data-role="partie-oubli"
      >
        {demande ? (
          /*
           * Ce message ne dit PAS qu'un compte existe. Il est écrit au
           * conditionnel exprès : dire « c'est envoyé » à une adresse
           * inconnue et « inconnue » à l'autre transformerait ce formulaire
           * en annuaire de la famille.
           */
          <Annonce urgence="urgent">
            <span data-role="oubli-envoye">
              Si cette adresse a un compte chez nous, un courriel vient de partir. Le lien qu’il
              contient est valable {DUREE_DU_LIEN_DE_RECUPERATION_HEURES} heures. Pensez à regarder
              dans les courriers indésirables.
            </span>
          </Annonce>
        ) : (
          <>
            {echec !== null && (
              <Annonce urgence="urgent" nature="refus">
                {echec}
              </Annonce>
            )}

            <form onSubmit={(evenement) => void demandeLeLien(evenement)} data-role="oubli">
              <Champ
                libelle="Adresse électronique"
                name="adresse-oubli"
                type="email"
                autoComplete="username"
                value={adresse}
                onChange={(evenement) => setAdresse(evenement.target.value)}
              />
              <button
                type="submit"
                data-role="principal"
                disabled={enCours || adresse.trim() === ''}
              >
                {enCours ? 'Envoi…' : 'Envoyer le lien'}
              </button>
            </form>
          </>
        )}

        <p>
          {/* `#/` et non `#/connexion` : sans session on y trouve l'entrée,
              avec session ses projets. Un seul lien, juste dans les deux cas. */}
          <a href="#/" data-role="vers-la-connexion">
            Revenir à l’écran d’entrée
          </a>
        </p>
      </Partie>
    </div>
  );
}
